import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useBreakpoint } from "@avenue-8/sidekick-ui";

const VERTEX_SHADER = `
  attribute float scale;
  uniform float uTime;
  uniform float uPointScale;

  void main() {
    vec3 p = position;
    float s = scale;

    p.y += (sin(p.x + uTime) * 0.5) + (cos(p.y + uTime) * 0.1) * 2.0;
    p.x += (sin(p.y + uTime) * 0.5);
    s += (sin(p.x + uTime) * 0.5) + (cos(p.y + uTime) * 0.1) * 2.0;

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);
    gl_PointSize = s * 15.0 * uPointScale * (1.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = `
  void main() {
    gl_FragColor = vec4(1.0, 1.0, 1.0, 0.6);
  }
`;

const NDC_CORNERS = [
  new THREE.Vector3(-1, 1, 0.5),
  new THREE.Vector3(1, 1, 0.5),
  new THREE.Vector3(-1, -1, 0.5),
  new THREE.Vector3(1, -1, 0.5),
];

class ParticleScene {
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private geometry: THREE.BufferGeometry;
  private material: THREE.ShaderMaterial;
  private particles: THREE.Points;
  private animationId: number | null = null;
  private resizeRafId: number | null = null;
  private resizeObserver: ResizeObserver;
  private lastFrameTime = 0;
  private positions: Float32Array;
  private gap: number;

  constructor(
    private canvas: HTMLCanvasElement,
    private amount: number,
  ) {
    const width = Math.max(canvas.clientWidth, 1);
    const height = Math.max(canvas.clientHeight, 1);

    this.camera = new THREE.PerspectiveCamera(75, width / height, 0.01, 1000);
    this.camera.position.set(0, 6, 5);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateMatrixWorld();

    this.scene = new THREE.Scene();

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // false = don't override canvas style — let CSS width:100%/height:100% control display size
    this.renderer.setSize(width, height, false);
    this.renderer.setClearColor(0x000000, 0);

    this.gap = this.computeGap();
    const count = amount * amount;
    this.positions = new Float32Array(count * 3);
    const scales = new Float32Array(count);

    this.fillPositions(this.gap);
    for (let j = 0; j < count; j++) scales[j] = 1;

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(this.positions, 3),
    );
    this.geometry.setAttribute("scale", new THREE.BufferAttribute(scales, 1));

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        uTime: { value: 0.0 },
        uPointScale: { value: this.gap / 0.45 },
      },
    });

    this.particles = new THREE.Points(this.geometry, this.material);
    this.scene.add(this.particles);

    this.animate = this.animate.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.resizeObserver = new ResizeObserver(() => {
      // Throttle via rAF so we update once per frame during continuous resizes
      if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.onResize();
      });
    });
    this.resizeObserver.observe(canvas);

    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.animate();
  }

  // Project the 4 screen corners onto the y=0 particle plane to compute
  // the gap needed so the grid always fills the entire viewport.
  private computeGap(): number {
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    let maxExtent = 15; // fallback

    for (const ndc of NDC_CORNERS) {
      const worldPos = ndc.clone().unproject(this.camera);
      const dir = worldPos.sub(this.camera.position).normalize();
      const ray = new THREE.Ray(this.camera.position, dir);
      const hit = new THREE.Vector3();
      if (ray.intersectPlane(plane, hit)) {
        maxExtent = Math.max(maxExtent, Math.abs(hit.x), Math.abs(hit.z));
      }
    }

    return (maxExtent * 2) / this.amount;
  }

  private fillPositions(gap: number) {
    let i = 0;
    for (let ix = 0; ix < this.amount; ix++) {
      for (let iy = 0; iy < this.amount; iy++) {
        this.positions[i] = ix * gap - (this.amount * gap) / 2;
        this.positions[i + 1] = 0;
        this.positions[i + 2] = iy * gap - (this.amount * gap) / 2;
        i += 3;
      }
    }
  }

  private animate(now = 0) {
    this.animationId = requestAnimationFrame(this.animate);
    if (now - this.lastFrameTime < 1000 / 30) return;
    this.lastFrameTime = now;
    this.material.uniforms.uTime.value += 0.02;
    this.camera.lookAt(this.scene.position);
    this.renderer.render(this.scene, this.camera);
  }

  private onResize() {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (width === 0 || height === 0) return;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);

    // Recompute grid to fill the new viewport
    this.gap = this.computeGap();
    this.fillPositions(this.gap);
    (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate =
      true;
    this.material.uniforms.uPointScale.value = this.gap / 0.45;
  }

  private onVisibilityChange() {
    if (document.hidden) {
      if (this.animationId !== null) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    } else {
      this.animate();
    }
  }

  destroy() {
    if (this.animationId !== null) cancelAnimationFrame(this.animationId);
    if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
    this.resizeObserver.disconnect();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.scene.remove(this.particles);
    this.geometry.dispose();
    this.material.dispose();
    this.renderer.dispose();
  }
}

export const ParticlesBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<ParticleScene | null>(null);
  const isLaptopDown = useBreakpoint("laptop");
  const amount = isLaptopDown ? 60 : 150;

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    try {
      sceneRef.current = new ParticleScene(canvas, amount);
    } catch (e) {
      console.error("Failed to initialize WebGL particles:", e);
    }

    return () => {
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
  }, [amount]);

  return (
    // height: 0 + sticky: the wrapper pins to the top as you scroll
    // but claims zero flex space, so content renders on top of the canvas
    <div
      style={{
        position: "sticky",
        top: 0,
        height: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};
